import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Clean Architecture Diagram - Concentric Circles
export function CleanArchitectureDiagram() {
  const [activeLayer, setActiveLayer] = useState(null);
  
  const layers = [
    {
      id: 'entities',
      name: 'Entities',
      color: '#8B5CF6',
      radius: 60,
      description: 'Бізнес-правила підприємства. Найстабільніший шар, не залежить від нічого зовнішнього.'
    },
    {
      id: 'usecases',
      name: 'Use Cases',
      color: '#6366F1',
      radius: 110,
      description: 'Бізнес-правила додатку. Оркеструє потік даних до/від Entities.'
    },
    {
      id: 'adapters',
      name: 'Interface Adapters',
      color: '#3B82F6',
      radius: 160,
      description: 'Controllers, Presenters, Gateways. Перетворює дані між Use Cases і зовнішнім світом.'
    },
    {
      id: 'frameworks',
      name: 'Frameworks & Drivers',
      color: '#0EA5E9',
      radius: 210,
      description: 'БД, UI, Web frameworks. Зовнішні деталі, які легко замінити.'
    }
  ];

  return (
    <div className="flex flex-col lg:flex-row items-center gap-8 p-6 bg-black/30 rounded-xl">
      <div className="relative">
        <svg width="440" height="440" viewBox="0 0 440 440">
          {/* Render layers from outside to inside */}
          {[...layers].reverse().map((layer, idx) => (
            <g key={layer.id}>
              <motion.circle
                cx="220"
                cy="220"
                r={layer.radius}
                fill={layer.color}
                fillOpacity={activeLayer === layer.id ? 0.9 : 0.6}
                stroke={activeLayer === layer.id ? '#fff' : 'transparent'}
                strokeWidth="3"
                className="cursor-pointer transition-all"
                onMouseEnter={() => setActiveLayer(layer.id)}
                onMouseLeave={() => setActiveLayer(null)}
                onClick={() => setActiveLayer(activeLayer === layer.id ? null : layer.id)}
                whileHover={{ scale: 1.02 }}
              />
              <text
                x="220"
                y={220 - layer.radius + 25}
                textAnchor="middle"
                fill="white"
                fontSize="12"
                fontWeight="bold"
                className="pointer-events-none"
              >
                {layer.name}
              </text>
            </g>
          ))}
          
          {/* Dependency arrows */}
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#22C55E" />
            </marker>
          </defs>
          
          {/* Arrow pointing inward */}
          <line x1="380" y1="220" x2="280" y2="220" stroke="#22C55E" strokeWidth="2" markerEnd="url(#arrowhead)" />
          <text x="400" y="225" fill="#22C55E" fontSize="10">Залежності</text>
          <text x="400" y="238" fill="#22C55E" fontSize="10">→ всередину</text>
        </svg>
      </div>
      
      <div className="flex-1 min-w-[280px]">
        <h3 className="text-xl font-bold text-foreground mb-4">Clean Architecture</h3>
        <AnimatePresence mode="wait">
          {activeLayer ? (
            <motion.div
              key={activeLayer}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-4 rounded-lg"
              style={{ backgroundColor: layers.find(l => l.id === activeLayer)?.color + '30' }}
            >
              <h4 className="font-bold text-lg mb-2" style={{ color: layers.find(l => l.id === activeLayer)?.color }}>
                {layers.find(l => l.id === activeLayer)?.name}
              </h4>
              <p className="text-foreground/80">{layers.find(l => l.id === activeLayer)?.description}</p>
            </motion.div>
          ) : (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-muted-foreground"
            >
              👆 Наведіть на шар, щоб дізнатися більше
            </motion.p>
          )}
        </AnimatePresence>
        
        <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
          <p className="text-green-400 text-sm font-medium">
            🔑 Головне правило: Залежності коду завжди спрямовані всередину!
          </p>
        </div>
      </div>
    </div>
  );
}

// Saga Pattern Diagram
export function SagaPatternDiagram() {
  const [activeStep, setActiveStep] = useState(null);
  const [showCompensation, setShowCompensation] = useState(false);
  
  const steps = [
    { id: 1, service: 'Order Service', action: 'Create Order', compensation: 'Cancel Order', color: '#8B5CF6' },
    { id: 2, service: 'Payment Service', action: 'Process Payment', compensation: 'Refund Payment', color: '#6366F1' },
    { id: 3, service: 'Inventory Service', action: 'Reserve Stock', compensation: 'Release Stock', color: '#3B82F6' },
    { id: 4, service: 'Shipping Service', action: 'Create Shipment', compensation: 'Cancel Shipment', color: '#0EA5E9' },
  ];

  return (
    <div className="p-6 bg-black/30 rounded-xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-foreground">Saga Pattern</h3>
        <button
          onClick={() => setShowCompensation(!showCompensation)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            showCompensation 
              ? 'bg-red-500/20 text-red-400 border border-red-500/50' 
              : 'bg-primary/20 text-primary border border-primary/50'
          }`}
        >
          {showCompensation ? '❌ Показати відкат' : '✅ Показати успішний шлях'}
        </button>
      </div>
      
      <div className="relative">
        {/* Flow visualization */}
        <div className="flex items-center justify-between gap-2 overflow-x-auto pb-4">
          {steps.map((step, idx) => (
            <div key={step.id} className="flex items-center">
              <motion.div
                className={`relative p-4 rounded-xl border-2 min-w-[140px] cursor-pointer transition-all ${
                  activeStep === step.id ? 'border-white shadow-lg' : 'border-white/20'
                }`}
                style={{ backgroundColor: step.color + '30' }}
                onMouseEnter={() => setActiveStep(step.id)}
                onMouseLeave={() => setActiveStep(null)}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <div className="text-xs text-white/60 mb-1">Крок {step.id}</div>
                <div className="font-bold text-white text-sm mb-2">{step.service}</div>
                <div 
                  className={`text-xs px-2 py-1 rounded ${
                    showCompensation 
                      ? 'bg-red-500/30 text-red-300' 
                      : 'bg-green-500/30 text-green-300'
                  }`}
                >
                  {showCompensation ? step.compensation : step.action}
                </div>
                
                {/* Event indicator */}
                <motion.div
                  className="absolute -bottom-6 left-1/2 transform -translate-x-1/2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: activeStep === step.id ? 1 : 0 }}
                >
                  <span className="text-xs text-yellow-400">📤 Event</span>
                </motion.div>
              </motion.div>
              
              {idx < steps.length - 1 && (
                <motion.div 
                  className="mx-2 flex items-center"
                  animate={{ 
                    x: showCompensation ? [-5, 0] : [0, 5],
                    transition: { repeat: Infinity, duration: 1 }
                  }}
                >
                  <svg width="40" height="20" viewBox="0 0 40 20">
                    <path 
                      d={showCompensation ? "M35 10 L5 10 M10 5 L5 10 L10 15" : "M5 10 L35 10 M30 5 L35 10 L30 15"}
                      stroke={showCompensation ? "#EF4444" : "#22C55E"} 
                      strokeWidth="2" 
                      fill="none"
                    />
                  </svg>
                </motion.div>
              )}
            </div>
          ))}
        </div>
        
        {/* Legend */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
            <h4 className="font-bold text-green-400 mb-1">✅ Успішний шлях</h4>
            <p className="text-sm text-foreground/70">Кожен сервіс виконує локальну транзакцію і публікує подію для наступного кроку.</p>
          </div>
          <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
            <h4 className="font-bold text-red-400 mb-1">❌ Компенсація</h4>
            <p className="text-sm text-foreground/70">При помилці виконуються зворотні операції для відкату всіх попередніх кроків.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Circuit Breaker State Machine
export function CircuitBreakerDiagram() {
  const [currentState, setCurrentState] = useState('closed');
  const [failureCount, setFailureCount] = useState(0);
  
  const states = {
    closed: {
      color: '#22C55E',
      label: 'CLOSED',
      description: 'Нормальний стан. Запити проходять до сервісу. При помилках збільшується лічильник.'
    },
    open: {
      color: '#EF4444',
      label: 'OPEN',
      description: 'Захисний стан. Запити відхиляються негайно без виклику сервісу. Чекаємо timeout.'
    },
    halfOpen: {
      color: '#F59E0B',
      label: 'HALF-OPEN',
      description: 'Тестовий стан. Пропускаємо обмежену кількість запитів для перевірки сервісу.'
    }
  };

  const simulateRequest = (success) => {
    if (currentState === 'closed') {
      if (success) {
        setFailureCount(0);
      } else {
        const newCount = failureCount + 1;
        setFailureCount(newCount);
        if (newCount >= 3) {
          setCurrentState('open');
          setTimeout(() => setCurrentState('halfOpen'), 3000);
        }
      }
    } else if (currentState === 'halfOpen') {
      if (success) {
        setCurrentState('closed');
        setFailureCount(0);
      } else {
        setCurrentState('open');
        setTimeout(() => setCurrentState('halfOpen'), 3000);
      }
    }
  };

  return (
    <div className="p-6 bg-black/30 rounded-xl">
      <h3 className="text-xl font-bold text-foreground mb-6">Circuit Breaker Pattern</h3>
      
      <div className="flex flex-col lg:flex-row items-center gap-8">
        {/* State Machine Visualization */}
        <div className="relative">
          <svg width="350" height="280" viewBox="0 0 350 280">
            {/* CLOSED state */}
            <motion.g
              animate={{ scale: currentState === 'closed' ? 1.1 : 1 }}
              style={{ transformOrigin: '80px 80px' }}
            >
              <circle cx="80" cy="80" r="45" fill={currentState === 'closed' ? '#22C55E' : '#22C55E50'} />
              <text x="80" y="75" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">CLOSED</text>
              <text x="80" y="90" textAnchor="middle" fill="white" fontSize="9">Нормальний</text>
            </motion.g>
            
            {/* OPEN state */}
            <motion.g
              animate={{ scale: currentState === 'open' ? 1.1 : 1 }}
              style={{ transformOrigin: '270px 80px' }}
            >
              <circle cx="270" cy="80" r="45" fill={currentState === 'open' ? '#EF4444' : '#EF444450'} />
              <text x="270" y="75" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">OPEN</text>
              <text x="270" y="90" textAnchor="middle" fill="white" fontSize="9">Захист</text>
            </motion.g>
            
            {/* HALF-OPEN state */}
            <motion.g
              animate={{ scale: currentState === 'halfOpen' ? 1.1 : 1 }}
              style={{ transformOrigin: '175px 210px' }}
            >
              <circle cx="175" cy="210" r="45" fill={currentState === 'halfOpen' ? '#F59E0B' : '#F59E0B50'} />
              <text x="175" y="205" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">HALF-OPEN</text>
              <text x="175" y="220" textAnchor="middle" fill="white" fontSize="9">Тест</text>
            </motion.g>
            
            {/* Arrows */}
            <defs>
              <marker id="arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#94A3B8" />
              </marker>
            </defs>
            
            {/* CLOSED → OPEN */}
            <path d="M 125 80 L 215 80" stroke="#94A3B8" strokeWidth="2" markerEnd="url(#arrow)" fill="none" />
            <text x="170" y="70" textAnchor="middle" fill="#94A3B8" fontSize="8">failures ≥ threshold</text>
            
            {/* OPEN → HALF-OPEN */}
            <path d="M 250 120 Q 230 165 195 185" stroke="#94A3B8" strokeWidth="2" markerEnd="url(#arrow)" fill="none" />
            <text x="240" y="160" fill="#94A3B8" fontSize="8">timeout</text>
            
            {/* HALF-OPEN → CLOSED */}
            <path d="M 135 190 Q 100 150 95 125" stroke="#22C55E" strokeWidth="2" markerEnd="url(#arrow)" fill="none" />
            <text x="90" y="160" fill="#22C55E" fontSize="8">success</text>
            
            {/* HALF-OPEN → OPEN */}
            <path d="M 215 190 Q 250 150 255 125" stroke="#EF4444" strokeWidth="2" markerEnd="url(#arrow)" fill="none" />
            <text x="260" y="160" fill="#EF4444" fontSize="8">failure</text>
          </svg>
        </div>
        
        {/* Interactive Controls */}
        <div className="flex-1 space-y-4">
          <div 
            className="p-4 rounded-lg border-2"
            style={{ 
              backgroundColor: states[currentState].color + '20',
              borderColor: states[currentState].color 
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-4 h-4 rounded-full animate-pulse"
                style={{ backgroundColor: states[currentState].color }}
              />
              <span className="font-bold" style={{ color: states[currentState].color }}>
                {states[currentState].label}
              </span>
            </div>
            <p className="text-sm text-foreground/80">{states[currentState].description}</p>
          </div>
          
          <div className="p-3 bg-white/5 rounded-lg">
            <div className="text-sm text-muted-foreground mb-2">Лічильник помилок: {failureCount}/3</div>
            <div className="w-full bg-black/30 rounded-full h-2">
              <div 
                className="h-2 rounded-full transition-all bg-red-500"
                style={{ width: `${(failureCount / 3) * 100}%` }}
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => simulateRequest(true)}
              disabled={currentState === 'open'}
              className="flex-1 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            >
              ✅ Успішний запит
            </button>
            <button
              onClick={() => simulateRequest(false)}
              disabled={currentState === 'open'}
              className="flex-1 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            >
              ❌ Помилка
            </button>
          </div>
          
          {currentState === 'open' && (
            <p className="text-sm text-yellow-400 animate-pulse">⏳ Очікування timeout... (3 сек)</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Database Sharding Diagram
export function ShardingDiagram() {
  const [selectedKey, setSelectedKey] = useState(null);
  
  const users = [
    { id: 'user_A', name: 'Anna', hash: 0 },
    { id: 'user_B', name: 'Boris', hash: 1 },
    { id: 'user_C', name: 'Катя', hash: 2 },
    { id: 'user_D', name: 'Денис', hash: 3 },
    { id: 'user_E', name: 'Євген', hash: 0 },
    { id: 'user_F', name: 'Федір', hash: 1 },
  ];
  
  const shards = [
    { id: 0, color: '#8B5CF6', name: 'Shard 0' },
    { id: 1, color: '#3B82F6', name: 'Shard 1' },
    { id: 2, color: '#22C55E', name: 'Shard 2' },
    { id: 3, color: '#F59E0B', name: 'Shard 3' },
  ];

  return (
    <div className="p-6 bg-black/30 rounded-xl">
      <h3 className="text-xl font-bold text-foreground mb-2">Database Sharding</h3>
      <p className="text-muted-foreground text-sm mb-6">Клікніть на користувача, щоб побачити на який шард потрапить його запис</p>
      
      <div className="flex flex-col lg:flex-row items-start gap-8">
        {/* Users */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Користувачі</h4>
          {users.map((user) => (
            <motion.div
              key={user.id}
              onClick={() => setSelectedKey(selectedKey === user.id ? null : user.id)}
              className={`p-3 rounded-lg cursor-pointer border-2 transition-all ${
                selectedKey === user.id 
                  ? 'border-white bg-white/10' 
                  : 'border-white/10 bg-white/5 hover:border-white/30'
              }`}
              whileHover={{ x: 5 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/30 flex items-center justify-center text-sm">
                  {user.name[0]}
                </div>
                <div>
                  <div className="font-medium text-foreground">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.id}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Hash Function */}
        <div className="flex flex-col items-center justify-center">
          <motion.div
            className="p-4 bg-yellow-500/20 rounded-xl border border-yellow-500/50 text-center"
            animate={selectedKey ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            <div className="text-yellow-400 text-sm font-medium mb-1">Hash Function</div>
            <code className="text-xs text-foreground/70">hash(user_id) % 4</code>
          </motion.div>
          
          {selectedKey && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 text-center"
            >
              <div className="text-xs text-muted-foreground">Результат:</div>
              <div className="text-2xl font-bold" style={{ color: shards[users.find(u => u.id === selectedKey)?.hash || 0].color }}>
                {users.find(u => u.id === selectedKey)?.hash}
              </div>
            </motion.div>
          )}
        </div>
        
        {/* Shards */}
        <div className="grid grid-cols-2 gap-3">
          {shards.map((shard) => {
            const shardUsers = users.filter(u => u.hash === shard.id);
            const isHighlighted = selectedKey && users.find(u => u.id === selectedKey)?.hash === shard.id;
            
            return (
              <motion.div
                key={shard.id}
                className={`p-4 rounded-xl border-2 min-w-[120px] transition-all ${
                  isHighlighted ? 'border-white shadow-lg' : 'border-white/10'
                }`}
                style={{ backgroundColor: shard.color + (isHighlighted ? '40' : '20') }}
                animate={isHighlighted ? { scale: 1.05 } : { scale: 1 }}
              >
                <div className="font-bold text-white text-sm mb-2">{shard.name}</div>
                <div className="space-y-1">
                  {shardUsers.map(user => (
                    <motion.div
                      key={user.id}
                      className={`text-xs px-2 py-1 rounded bg-black/30 ${
                        selectedKey === user.id ? 'ring-2 ring-white' : ''
                      }`}
                      animate={selectedKey === user.id ? { scale: [1, 1.1, 1] } : {}}
                    >
                      {user.name}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
      
      <div className="mt-6 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
        <p className="text-blue-400 text-sm">
          💡 <strong>Hash-based sharding</strong> рівномірно розподіляє дані між шардами, але ускладнює range queries.
        </p>
      </div>
    </div>
  );
}

// API Gateway / BFF Pattern
export function ApiGatewayDiagram() {
  const [activeClient, setActiveClient] = useState(null);
  
  const clients = [
    { id: 'mobile', name: 'Mobile App', icon: '📱', bff: 'Mobile BFF' },
    { id: 'web', name: 'Web App', icon: '💻', bff: 'Web BFF' },
    { id: 'partner', name: 'Partner API', icon: '🤝', bff: 'Public API' },
  ];
  
  const services = [
    { id: 'users', name: 'Users', color: '#8B5CF6' },
    { id: 'orders', name: 'Orders', color: '#3B82F6' },
    { id: 'products', name: 'Products', color: '#22C55E' },
    { id: 'payments', name: 'Payments', color: '#F59E0B' },
  ];

  return (
    <div className="p-6 bg-black/30 rounded-xl">
      <h3 className="text-xl font-bold text-foreground mb-2">API Gateway & BFF Pattern</h3>
      <p className="text-muted-foreground text-sm mb-6">Наведіть на клієнта, щоб побачити маршрут запиту</p>
      
      <div className="space-y-8">
        {/* Clients Layer */}
        <div className="flex justify-center gap-6">
          {clients.map((client) => (
            <motion.div
              key={client.id}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                activeClient === client.id 
                  ? 'border-primary bg-primary/20' 
                  : 'border-white/10 bg-white/5'
              }`}
              onMouseEnter={() => setActiveClient(client.id)}
              onMouseLeave={() => setActiveClient(null)}
              whileHover={{ y: -5 }}
            >
              <div className="text-3xl text-center mb-2">{client.icon}</div>
              <div className="text-sm font-medium text-center text-foreground">{client.name}</div>
            </motion.div>
          ))}
        </div>
        
        {/* Connection Lines */}
        <div className="relative h-8">
          <svg className="w-full h-full" preserveAspectRatio="none">
            {activeClient && (
              <motion.line
                x1="50%"
                y1="0"
                x2="50%"
                y2="100%"
                stroke="#8B5CF6"
                strokeWidth="3"
                strokeDasharray="5,5"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
              />
            )}
          </svg>
        </div>
        
        {/* BFF Layer */}
        <div className="flex justify-center">
          <motion.div
            className={`px-8 py-4 rounded-xl border-2 transition-all ${
              activeClient 
                ? 'border-yellow-500 bg-yellow-500/20' 
                : 'border-white/10 bg-white/5'
            }`}
            animate={activeClient ? { scale: 1.05 } : { scale: 1 }}
          >
            <div className="text-center">
              <div className="text-yellow-400 text-sm font-medium mb-1">API Gateway / BFF</div>
              <div className="text-foreground font-bold">
                {activeClient ? clients.find(c => c.id === activeClient)?.bff : 'Backend for Frontend'}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Auth • Rate Limiting • Caching • Routing
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Connection to Services */}
        <div className="relative h-8">
          <svg className="w-full h-full" preserveAspectRatio="none">
            {activeClient && (
              <>
                {services.map((_, idx) => (
                  <motion.line
                    key={idx}
                    x1="50%"
                    y1="0"
                    x2={`${15 + idx * 23}%`}
                    y2="100%"
                    stroke={services[idx].color}
                    strokeWidth="2"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 0.2 + idx * 0.1 }}
                  />
                ))}
              </>
            )}
          </svg>
        </div>
        
        {/* Microservices Layer */}
        <div className="flex justify-center gap-4">
          {services.map((service, idx) => (
            <motion.div
              key={service.id}
              className="p-3 rounded-lg border border-white/10"
              style={{ backgroundColor: service.color + '20' }}
              animate={activeClient ? { scale: [1, 1.05, 1], transition: { delay: 0.3 + idx * 0.1 } } : {}}
            >
              <div className="w-3 h-3 rounded-full mb-2 mx-auto" style={{ backgroundColor: service.color }} />
              <div className="text-xs font-medium text-foreground text-center">{service.name}</div>
            </motion.div>
          ))}
        </div>
      </div>
      
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
          <p className="text-green-400 text-sm">✅ Кожен клієнт отримує оптимізований API</p>
        </div>
        <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
          <p className="text-blue-400 text-sm">🔒 Централізована безпека та моніторинг</p>
        </div>
      </div>
    </div>
  );
}

// Bounded Context Diagram
export function BoundedContextDiagram() {
  const [activeContext, setActiveContext] = useState(null);
  
  const contexts = [
    {
      id: 'ordering',
      name: 'Ordering Context',
      color: '#8B5CF6',
      x: 50,
      y: 50,
      entities: ['Order', 'Customer', 'Payment'],
      description: 'Відповідає за створення та управління замовленнями'
    },
    {
      id: 'inventory',
      name: 'Inventory Context',
      color: '#22C55E',
      x: 250,
      y: 50,
      entities: ['Product', 'Stock', 'Warehouse'],
      description: 'Управління товарними запасами та складами'
    },
    {
      id: 'shipping',
      name: 'Shipping Context',
      color: '#F59E0B',
      x: 150,
      y: 180,
      entities: ['Shipment', 'Delivery', 'Carrier'],
      description: 'Логістика та доставка замовлень'
    },
  ];

  return (
    <div className="p-6 bg-black/30 rounded-xl">
      <h3 className="text-xl font-bold text-foreground mb-2">Bounded Contexts</h3>
      <p className="text-muted-foreground text-sm mb-6">Кожен контекст має свою модель домену та мову</p>
      
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="relative">
          <svg width="400" height="300" viewBox="0 0 400 300">
            {/* Context boxes */}
            {contexts.map((ctx) => (
              <g key={ctx.id}>
                <motion.rect
                  x={ctx.x}
                  y={ctx.y}
                  width="140"
                  height="100"
                  rx="10"
                  fill={ctx.color + '30'}
                  stroke={activeContext === ctx.id ? '#fff' : ctx.color}
                  strokeWidth={activeContext === ctx.id ? 3 : 2}
                  strokeDasharray={activeContext === ctx.id ? '0' : '5,5'}
                  className="cursor-pointer"
                  onMouseEnter={() => setActiveContext(ctx.id)}
                  onMouseLeave={() => setActiveContext(null)}
                  whileHover={{ scale: 1.02 }}
                />
                <text 
                  x={ctx.x + 70} 
                  y={ctx.y + 25} 
                  textAnchor="middle" 
                  fill={ctx.color} 
                  fontSize="11" 
                  fontWeight="bold"
                  className="pointer-events-none"
                >
                  {ctx.name.split(' ')[0]}
                </text>
                {ctx.entities.map((entity, idx) => (
                  <text
                    key={entity}
                    x={ctx.x + 70}
                    y={ctx.y + 50 + idx * 16}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize="10"
                    className="pointer-events-none"
                  >
                    • {entity}
                  </text>
                ))}
              </g>
            ))}
            
            {/* Integration arrows */}
            <defs>
              <marker id="contextArrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="#64748B" />
              </marker>
            </defs>
            
            {/* Ordering → Inventory */}
            <path d="M 190 100 L 250 100" stroke="#64748B" strokeWidth="2" markerEnd="url(#contextArrow)" fill="none" />
            <text x="220" y="90" textAnchor="middle" fill="#64748B" fontSize="8">check stock</text>
            
            {/* Ordering → Shipping */}
            <path d="M 120 150 L 170 180" stroke="#64748B" strokeWidth="2" markerEnd="url(#contextArrow)" fill="none" />
            <text x="130" y="175" fill="#64748B" fontSize="8">create</text>
            
            {/* Inventory → Shipping */}
            <path d="M 320 150 L 270 180" stroke="#64748B" strokeWidth="2" markerEnd="url(#contextArrow)" fill="none" />
            <text x="305" y="175" fill="#64748B" fontSize="8">reserve</text>
          </svg>
        </div>
        
        <div className="flex-1">
          <AnimatePresence mode="wait">
            {activeContext ? (
              <motion.div
                key={activeContext}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-4 rounded-xl"
                style={{ backgroundColor: contexts.find(c => c.id === activeContext)?.color + '20' }}
              >
                <h4 
                  className="font-bold text-lg mb-2"
                  style={{ color: contexts.find(c => c.id === activeContext)?.color }}
                >
                  {contexts.find(c => c.id === activeContext)?.name}
                </h4>
                <p className="text-foreground/80 mb-3">
                  {contexts.find(c => c.id === activeContext)?.description}
                </p>
                <div className="text-sm text-muted-foreground">
                  <strong>Entities:</strong> {contexts.find(c => c.id === activeContext)?.entities.join(', ')}
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-4 bg-white/5 rounded-xl"
              >
                <p className="text-muted-foreground">👆 Наведіть на контекст для деталей</p>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-1 border border-dashed border-white/50" />
                    <span className="text-xs text-muted-foreground">Межа контексту</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-0.5 bg-slate-500" />
                    <span className="text-xs text-muted-foreground">Інтеграція між контекстами</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
            <p className="text-purple-400 text-sm">
              🗣️ <strong>Ubiquitous Language:</strong> Кожен контекст має свою термінологію. "Customer" в Ordering ≠ "Customer" в Shipping
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Load Balancing Diagram
export function LoadBalancingDiagram() {
  const [algorithm, setAlgorithm] = useState('round-robin');
  const [requests, setRequests] = useState([]);
  const [serverLoads, setServerLoads] = useState([0, 0, 0]);
  
  const algorithms = [
    { id: 'round-robin', name: 'Round Robin', description: 'Послідовний розподіл по черзі' },
    { id: 'least-conn', name: 'Least Connections', description: 'До сервера з найменшим навантаженням' },
    { id: 'random', name: 'Random', description: 'Випадковий вибір сервера' },
  ];
  
  let roundRobinIndex = 0;
  
  const sendRequest = () => {
    let targetServer;
    
    switch (algorithm) {
      case 'round-robin':
        targetServer = roundRobinIndex % 3;
        roundRobinIndex++;
        break;
      case 'least-conn':
        targetServer = serverLoads.indexOf(Math.min(...serverLoads));
        break;
      case 'random':
        targetServer = Math.floor(Math.random() * 3);
        break;
      default:
        targetServer = 0;
    }
    
    const newRequest = {
      id: Date.now(),
      target: targetServer,
    };
    
    setRequests(prev => [...prev.slice(-5), newRequest]);
    setServerLoads(prev => {
      const newLoads = [...prev];
      newLoads[targetServer] = Math.min(100, newLoads[targetServer] + 20);
      return newLoads;
    });
    
    // Decay load over time
    setTimeout(() => {
      setServerLoads(prev => prev.map(l => Math.max(0, l - 10)));
    }, 1000);
  };

  return (
    <div className="p-6 bg-black/30 rounded-xl">
      <h3 className="text-xl font-bold text-foreground mb-4">Load Balancing</h3>
      
      <div className="flex flex-wrap gap-2 mb-6">
        {algorithms.map((alg) => (
          <button
            key={alg.id}
            onClick={() => setAlgorithm(alg.id)}
            className={`px-4 py-2 rounded-lg text-sm transition-all ${
              algorithm === alg.id
                ? 'bg-primary text-white'
                : 'bg-white/5 text-muted-foreground hover:bg-white/10'
            }`}
          >
            {alg.name}
          </button>
        ))}
      </div>
      
      <p className="text-sm text-muted-foreground mb-6">
        {algorithms.find(a => a.id === algorithm)?.description}
      </p>
      
      <div className="flex flex-col items-center gap-8">
        {/* Clients */}
        <div className="flex items-center gap-4">
          <motion.button
            onClick={sendRequest}
            className="px-6 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-all"
            whileTap={{ scale: 0.95 }}
          >
            📤 Відправити запит
          </motion.button>
        </div>
        
        {/* Load Balancer */}
        <motion.div
          className="px-8 py-4 bg-yellow-500/20 rounded-xl border-2 border-yellow-500"
          animate={requests.length > 0 ? { scale: [1, 1.05, 1] } : {}}
        >
          <div className="text-center">
            <div className="text-yellow-400 font-bold">Load Balancer</div>
            <div className="text-xs text-muted-foreground">{algorithms.find(a => a.id === algorithm)?.name}</div>
          </div>
        </motion.div>
        
        {/* Servers */}
        <div className="flex gap-6">
          {[0, 1, 2].map((serverIdx) => (
            <motion.div
              key={serverIdx}
              className={`p-4 rounded-xl border-2 w-28 transition-all ${
                requests.slice(-1)[0]?.target === serverIdx
                  ? 'border-green-500 bg-green-500/20'
                  : 'border-white/10 bg-white/5'
              }`}
              animate={requests.slice(-1)[0]?.target === serverIdx ? { y: [0, -5, 0] } : {}}
            >
              <div className="text-center mb-3">
                <div className="text-2xl">🖥️</div>
                <div className="text-sm font-medium text-foreground">Server {serverIdx + 1}</div>
              </div>
              <div className="w-full bg-black/30 rounded-full h-2">
                <motion.div
                  className="h-2 rounded-full bg-gradient-to-r from-green-500 to-red-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${serverLoads[serverIdx]}%` }}
                />
              </div>
              <div className="text-xs text-center text-muted-foreground mt-1">
                {serverLoads[serverIdx]}% load
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
